import { useQueryClient, InfiniteData } from "@tanstack/react-query";
import { Contact } from "../types/contact";
import { ChatItem, RoomInfo } from "../types/chat";
import { DataRecord } from "../types";
import { useCreateChatRoom } from "./queries/useCreateChatRoom";
import { useJoinChatRoom } from "./queries/useJoinChatRoom";
import { useEffect } from "react";
import { useSearchParamsV6 } from "./useSearchParamsV6";

/**
 * Hook để lấy thông tin phòng dựa trên tham số URL
 * Xử lý cả việc tạo phòng mới và tham gia phòng hiện có
 *
 * @param id - ID liên hệ/chat từ URL params
 * @param t - Loại tab từ URL params ("contact" | "recent" | "")
 * @returns Dữ liệu phòng, trạng thái loading và error
 */
export const useGetInfoRoom = (id: string, t: string) => {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParamsV6();

  let contactItem: Contact | undefined;
  let chatItem: ChatItem | undefined;

  // Xác định cache nào cần query dựa trên loại tab (chỉ khi có id)
  if (id && t === "contact") {
    // Lấy contact từ cache - thử các query key khác nhau có thể
    // Key thực tế là ["get-contacts", keyword] nhưng chúng ta không biết keyword ở đây
    // Vì vậy cần duyệt qua tất cả các query để tìm contacts
    const queryCache = queryClient.getQueryCache();
    const contactQueries = queryCache.findAll({ queryKey: ["get-contacts"] });

    // Lấy dữ liệu contacts mới nhất
    if (contactQueries.length > 0) {
      const latestQuery = contactQueries[contactQueries.length - 1];
      const contactsData = latestQuery.state.data as InfiniteData<DataRecord<Contact[]>> | undefined;
      contactItem = contactsData?.pages?.flatMap((page) => page.Data || []).find((item) => item.Id === id);
    }
  } else if (id && (t === "recent" || t === "")) {
    // Lấy chats từ cache
    const queryCache = queryClient.getQueryCache();
    const chatQueries = queryCache.findAll({ queryKey: ["get-chats"] });

    if (chatQueries.length > 0) {
      const latestQuery = chatQueries[chatQueries.length - 1];
      const chatsData = latestQuery.state.data as ChatItem[] | undefined;
      chatItem = chatsData?.find((item) => item.PID === id);
    }
  }

  // Xác định xem cần tạo phòng mới hay tham gia phòng hiện có
  const needsRoomCreation = !!id && contactItem?.RoomId === null;
  const existingRoomId = contactItem?.RoomId || chatItem?.ID || "";

  // Tạo phòng nếu contact chưa có RoomId
  const {
    data: createdRoomData,
    isLoading: isCreateRoomLoading,
    isError: isCreateRoomError,
    isSuccess: isCreateRoomSuccess,
  } = useCreateChatRoom(
    {
      pID: contactItem?.Id || "",
      roomName: contactItem?.ContactName || "",
      roomType: "U",
    },
    needsRoomCreation,
  );

  // Sau khi tạo phòng, invalidate chats cache và cập nhật URL để chọn phòng mới
  useEffect(() => {
    if (isCreateRoomSuccess && createdRoomData?.Data?.RoomId) {
      // Invalidate chats cache để phòng mới xuất hiện trong danh sách
      queryClient.invalidateQueries({ queryKey: ["get-chats"] });

      // Cập nhật URL để chọn phòng vừa tạo
      const params = new URLSearchParams();
      params.set("roomId", createdRoomData.Data.RoomId);
      setSearchParams(params);
    }
  }, [isCreateRoomSuccess, createdRoomData?.Data?.RoomId, queryClient, setSearchParams]);

  // Tham gia phòng nếu contact đã có RoomId hoặc nếu là chat item

  const {
    data: joinedRoomData,
    isLoading: isJoinRoomLoading,
    isError: isJoinRoomError,
  } = useJoinChatRoom(
    {
      roomId: existingRoomId,
    },
    !!id && !!existingRoomId && !needsRoomCreation, // Chỉ tham gia nếu có roomId và không đang tạo
  );

  // Trả về sớm nếu không có id - để ChatWindow xử lý tham số roomId trực tiếp
  if (!id) {
    return {
      roomData: undefined,
      isLoading: false,
      isError: false,
    };
  }

  // Xác định trạng thái loading và dữ liệu phòng
  const isLoading = isCreateRoomLoading || isJoinRoomLoading;
  const isError = isCreateRoomError || isJoinRoomError;
  const roomData = needsRoomCreation ? createdRoomData?.Data : joinedRoomData?.Data;

  return {
    roomData: roomData as RoomInfo | undefined,
    isLoading,
    isError,
  };
};
